import {
    Box,
    Drawer,
    Grid,
    IconButton,
    Stack,
    Divider,
    Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
    GoogleMap,
    MarkerF,
} from '@react-google-maps/api';
import _ from "lodash";
import { When } from "react-if";
import { useEffect, useRef, useState } from "react";

import { EventTypes, UserTypes } from "../../types";
import { LatLng } from "../event-map";
import { markerConstants } from "../../constants/marker.constants";

interface Props {
    onClose(): void,
    visible: boolean,
    event: EventTypes.Model
}

export const UserMapLocationDetailsDrawer: React.FC<Props> = ({
    onClose,
    visible,
    event
}) => {
    const mapRef = useRef<google.maps.Map | null>(null);

    const isMountedRef = useRef<boolean>(false);

    const user = event?.content as UserTypes.Model;

    const [center, setCenter] = useState<LatLng>(
        {
            lat: Number(event?.latitude),
            lng: Number(event?.longitude)
        }
    );
    const [zoom, setZoom] = useState<number>(18);

    const handleMapLoad = (map: google.maps.Map) => {
        mapRef.current = map;
    };

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    return (
        <Drawer
            anchor="bottom"
            open={visible}
            onClose={onClose}
        >
            <Box
                sx={{ minHeight: { xs: "500px", md: "500px" } }}
            >
                <Stack direction={'row'} justifyContent={'space-between'} mt={1} style={{ padding: 10 }}>
                    <Typography fontSize={24} fontWeight={400} textAlign={'left'}>
                        User Mobile Location Details
                    </Typography>
                    <IconButton
                        onClick={onClose}
                        sx={{ alignSelf: 'flex-start' }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Stack>

                <Divider />

                <Box style={{ padding: 24, marginLeft: 2 }} mt={1}>
                    <Grid container spacing={2}>
                        <Grid container spacing={2}>
                            <Grid container spacing={2} p={2} md={4} sm={12} alignContent={'flex-start'}>
                                <Grid item md={12} sm={12} xs={12}>
                                    <Typography mb={2} fontWeight={400}>
                                        Personal Information
                                    </Typography>
                                </Grid>
                                <Grid item md={6} sm={12}>
                                    <Typography fontSize={14} fontWeight={500}>
                                        Full Name
                                    </Typography>
                                    <Typography fontSize={14} fontWeight={400} mt={1}>
                                        {user?.firstName} {user?.lastName}
                                    </Typography>
                                </Grid>
                                <Grid item md={6} sm={12}>
                                    <Typography fontSize={14} fontWeight={500}>
                                        Backup Email
                                    </Typography>
                                    <Typography fontSize={14} fontWeight={400} mt={1}>
                                        {user?.backupEmail || '----'}
                                    </Typography>
                                </Grid>

                                <Grid item md={6} sm={12}>
                                    <Typography fontSize={14} fontWeight={500}>
                                        Phone Number
                                    </Typography>
                                    <Typography fontSize={14} fontWeight={400} mt={1}>
                                        {user?.phoneNumber || '----'}
                                    </Typography>
                                </Grid>

                                <Grid item md={6} sm={12}>
                                    <Typography fontSize={14} fontWeight={500}>
                                        Team Leader
                                    </Typography>
                                    <Typography fontSize={14} fontWeight={400} mt={1}>
                                        {user?.teamLeader?.fullname || '----'}
                                    </Typography>
                                </Grid>
                            </Grid>

                            <Divider orientation="vertical" flexItem />

                            <Grid container spacing={2} p={2} md={4} sm={12} alignContent={'flex-start'}>
                                <Grid item md={12} sm={12} xs={12}>
                                    <Typography mb={2} fontWeight={400}>
                                        Emergency Contact Information
                                    </Typography>
                                </Grid>
                                <Grid item md={6} sm={12}>
                                    <Typography fontSize={14} fontWeight={500}>
                                        First Name
                                    </Typography>
                                    <Typography fontSize={14} fontWeight={400} mt={1}>
                                        {user?.emergencyContact?.contactFirstName || '----'}
                                    </Typography>
                                </Grid>
                                <Grid item md={6} sm={12}>
                                    <Typography fontSize={14} fontWeight={500}>
                                        Last Name
                                    </Typography>
                                    <Typography fontSize={14} fontWeight={400} mt={1}>
                                        {user?.emergencyContact?.contactLastName || '----'}
                                    </Typography>
                                </Grid>
                                <Grid item md={6} sm={12}>
                                    <Typography fontSize={14} fontWeight={500}>
                                        Phone Number
                                    </Typography>
                                    <Typography fontSize={14} fontWeight={400} mt={1}>
                                        {user?.emergencyContact?.contactPhoneNumber || '----'}
                                    </Typography>
                                </Grid>

                                <Divider orientation="horizontal" />

                                <Grid item md={12} sm={12} xs={12} mt={4}>
                                    <Typography mb={2} fontWeight={400}>
                                        SOS Settings
                                    </Typography>
                                </Grid>

                                <Grid item md={4} sm={12}>
                                    <Typography fontSize={14} fontWeight={500}>
                                        Alert SOS Connections
                                    </Typography>
                                    <Typography fontSize={14} fontWeight={400} mt={1}>
                                        {user?.sosSettings?.alertSOSConnections ? 'True' : 'False'}
                                    </Typography>
                                </Grid>
                                <Grid item md={6} sm={12}>
                                    <Typography fontSize={14} fontWeight={500}>
                                        Dispatch Enabled
                                    </Typography>
                                    <Typography fontSize={14} fontWeight={400} mt={1}>
                                        {user?.sosSettings?.dispatchEnabled ? 'True' : 'False'}
                                    </Typography>
                                </Grid>
                            </Grid>

                            <Divider orientation="vertical" flexItem />

                            <Grid container spacing={2} p={2} md={4} sm={12} alignContent={'flex-start'}>
                                <Grid item md={12} sm={12} xs={12}>
                                    <Typography mb={2} fontWeight={400}>
                                        Mobile Location
                                    </Typography>
                                    <Typography mb={2} fontWeight={500}>
                                        {user?.mobileLocation?.address}
                                    </Typography>
                                    <When condition={event?.latitude && event?.longitude}>
                                        <Grid item md={12} sm={12} xs={12}>
                                            <Typography mb={2} fontWeight={400}>Last Location</Typography>
                                            <div style={{ width: '100%', height: 400 }}>
                                                    <GoogleMap
                                                        onLoad={handleMapLoad}
                                                        options={{
                                                            disableDefaultUI: true,
                                                            zoomControl: true,
                                                            fullscreenControl: true,
                                                            mapTypeControl: true,
                                                            streetViewControl: true,
                                                            scaleControl: true,
                                                            rotateControl: true,
                                                            clickableIcons: false,
                                                            scrollwheel: true,
                                                            draggable: true,
                                                            draggableCursor: 'default',
                                                            draggingCursor: 'pointer',
                                                            keyboardShortcuts: true,
                                                            disableDoubleClickZoom: false,
                                                            noClear: false,
                                                            styles: [
                                                                {
                                                                    featureType: 'poi',
                                                                    elementType: 'labels',
                                                                    stylers: [
                                                                        {
                                                                            visibility: 'off',
                                                                        },
                                                                    ],
                                                                },
                                                            ],
                                                        }}
                                                        mapContainerStyle={{ height: '100%', width: '100%' }}
                                                        zoom={zoom}
                                                        center={center}
                                                    >
                                                        <MarkerF
                                                            position={{
                                                                lat: Number(event?.latitude),
                                                                lng: Number(event?.longitude)
                                                            }}
                                                            options={{
                                                                icon: {
                                                                    url: markerConstants['userMobileLocation'],
                                                                    scaledSize: new google.maps.Size(10, 10),
                                                                    scale: 1,
                                                                    origin: new google.maps.Point(0, 0),
                                                                }
                                                            }}
                                                        />
                                                    </GoogleMap>
                                            </div>
                                        </Grid>
                                    </When>
                                </Grid>
                            </Grid>


                        </Grid>
                    </Grid>
                </Box>
            </Box>
        </Drawer>
    )
}